import {
  ApiProperty,
  ApiPropertyOptional,
  IntersectionType,
} from '@nestjs/swagger';
import { SupplierInterface } from '../interfaces/supplier.interface';
import { CommonEntityDto } from '../../common/dto/common-entity.dto';
import { ArchivableEntityDto } from '../../common/dto/archivable-entity.dto';
import {
  IsEmail,
  IsOptional,
  IsString,
  MaxLength,
  MinLength,
} from 'class-validator';
import { Exclude, Expose } from 'class-transformer';
import { SupplierRegionDto } from '../../supplier-region/dto/supplier-region.dto';
import {
  SUPPLIER_EXTERNAL_ID_MIN_LENGTH,
  SUPPLIER_EXTERNAL_ID_MAX_LENGTH,
  SUPPLIER_NAME_MIN_LENGTH,
  SUPPLIER_NAME_MAX_LENGTH,
  SUPPLIER_DESCRIPTION_MIN_LENGTH,
  SUPPLIER_DESCRIPTION_MAX_LENGTH,
  SUPPLIER_CONTACT_INFO_MIN_LENGTH,
  SUPPLIER_CONTACT_INFO_MAX_LENGTH,
  SUPPLIER_PHONE_MIN_LENGTH,
  SUPPLIER_PHONE_MAX_LENGTH,
} from '../constants/supplier-validation.constants';
import { SupplierRepresentativeDto } from '../../supplier-representative/dto/supplier-representative.dto';

@Exclude()
export class SupplierDto
  extends IntersectionType(CommonEntityDto, ArchivableEntityDto)
  implements SupplierInterface
{
  @ApiProperty({
    title: 'External ID',
    description: 'Identifier manually carried over from external system.',
    minLength: SUPPLIER_EXTERNAL_ID_MIN_LENGTH,
    maxLength: SUPPLIER_EXTERNAL_ID_MAX_LENGTH,
  })
  @IsString()
  @MinLength(SUPPLIER_EXTERNAL_ID_MIN_LENGTH)
  @MaxLength(SUPPLIER_EXTERNAL_ID_MAX_LENGTH)
  @Expose()
  externalId!: string;

  @ApiProperty({
    title: 'Name',
    description: 'The name of the Supplier',
    minLength: SUPPLIER_NAME_MIN_LENGTH,
    maxLength: SUPPLIER_NAME_MAX_LENGTH,
  })
  @IsString()
  @MinLength(SUPPLIER_NAME_MIN_LENGTH)
  @MaxLength(SUPPLIER_NAME_MAX_LENGTH)
  @Expose()
  name!: string;

  @ApiProperty({
    title: 'Description',
    description: 'The description of the Supplier',
    minLength: SUPPLIER_DESCRIPTION_MIN_LENGTH,
    maxLength: SUPPLIER_DESCRIPTION_MAX_LENGTH,
  })
  @IsString()
  @MinLength(SUPPLIER_DESCRIPTION_MIN_LENGTH)
  @MaxLength(SUPPLIER_DESCRIPTION_MAX_LENGTH)
  @Expose()
  description!: string;

  @ApiProperty({
    title: 'Email',
    description: 'The email of the Supplier',
  })
  @IsEmail()
  @Expose()
  email!: string;

  @ApiProperty({
    title: 'Phone',
    description: 'The US phone number of the Supplier',
    minLength: SUPPLIER_PHONE_MIN_LENGTH,
    maxLength: SUPPLIER_PHONE_MAX_LENGTH,
  })
  @IsString()
  @MinLength(SUPPLIER_PHONE_MIN_LENGTH)
  @MaxLength(SUPPLIER_PHONE_MAX_LENGTH)
  @Expose()
  phone!: string;

  @ApiProperty({
    title: 'Contact Info',
    description: 'Misc other contact information or notes',
    minLength: SUPPLIER_CONTACT_INFO_MIN_LENGTH,
    maxLength: SUPPLIER_CONTACT_INFO_MAX_LENGTH,
  })
  @IsString()
  @MinLength(SUPPLIER_CONTACT_INFO_MIN_LENGTH)
  @MaxLength(SUPPLIER_CONTACT_INFO_MAX_LENGTH)
  @Expose()
  contactInfo!: string;

  @ApiPropertyOptional({
    type: () => [SupplierRegionDto],
    title: 'Associated Supplier Regions',
    description: 'The regions associated with this supplier',
  })
  @IsOptional()
  @Expose()
  supplierRegions?: SupplierRegionDto[];

  @ApiPropertyOptional({
    type: () => [SupplierRepresentativeDto],
    title: 'Associated Supplier Representatve',
    description: 'The Supplier Representatives associeted with this supplier',
  })
  @IsOptional()
  @Expose()
  supplierRepresentative?: SupplierRepresentativeDto[];
}
